import { useCallback } from "react";

import { Action, Run, RunState, Stack, Module, RunType } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";

import useHasEntityWriteAccess from "./useHasEntityWriteAccess";
import useHasEntityReadAccess from "./useHasEntityReadAccess";

const STOP_ACCESS_TYPES = [Action.RunStop];
const STOP_BLOCKING_ACCESS_TYPES = [Action.RunStopBlocking];

const hasStopAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(STOP_ACCESS_TYPES, viewer, spaceId);

const hasStopBlockingAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(STOP_BLOCKING_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook to determine if the current user can stop a run.
 *
 * For non-blocking runs (e.g. Proposed), users need either:
 * - Stack/Module read access
 * - RunStop permission
 *
 * For blocking runs (Tracked, Destroy, Task), users need either:
 * - Stack/Module write access
 * - RunStopBlocking permission
 *
 * @returns A function that takes a run and entity (Stack or Module) and returns an AccessResult
 */
const useCanStopRun = (): ((run: Run, entity: Stack | Module) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const hasEntityWriteAccess = useHasEntityWriteAccess();
  const hasEntityReadAccess = useHasEntityReadAccess();

  return useCallback(
    (run: Run, entity: Stack | Module) => {
      if (
        run.state !== RunState.Initializing &&
        run.state !== RunState.Planning &&
        run.state !== RunState.Applying &&
        run.state !== RunState.Performing &&
        run.state !== RunState.Destroying
      ) {
        return { result: false };
      }

      const isBlocking =
        run.type === RunType.Tracked || run.type === RunType.Destroy || run.type === RunType.Task;

      const spaceId = entity.spaceDetails?.id || entity.space;

      if (!isBlocking) {
        if (hasEntityReadAccess(entity)) {
          return { result: true };
        }

        if (hasStopAccess(viewer, spaceId)) {
          return { result: true };
        }

        return { result: false, message: NO_ACCESS_MESSAGE };
      }

      if (hasEntityWriteAccess(entity)) {
        return { result: true };
      }

      if (hasStopBlockingAccess(viewer, spaceId)) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [viewer, hasEntityReadAccess, hasEntityWriteAccess]
  );
};

export default useCanStopRun;
